import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { File } from "@/types/breeze/File";
import { motion } from "motion/react";
import { useSecureImage } from "@/hooks/useSecureFile";
import { resolveMediaCategory } from "@/utils/fileUtils";
import { Box, Flex, Text, IconButton } from "@radix-ui/themes";

interface ImageGalleryOverlayProps {
  files: File[];
  initialIndex?: number;
  onClose: () => void;
}

// Component for individual secure images to handle loading independently
function SecureImage({
  file,
  alt,
  isMain = false,
  style = {},
}: {
  file: File;
  alt: string;
  isMain?: boolean;
  style?: React.CSSProperties;
}) {
  const { blobUrl, loading, error } = useSecureImage(file.id, true);
  const category = resolveMediaCategory(file);

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          ...style,
          backgroundColor: "var(--gray-8)",
        }}
      >
        <Box
          style={{
            width: "24px",
            height: "24px",
            border: "2px solid var(--blue-9)",
            borderTop: "2px solid transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
      </Flex>
    );
  }

  if (error || !blobUrl) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          ...style,
          backgroundColor: "var(--gray-8)",
        }}
      >
        <Text size="2" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
          Failed to load
        </Text>
      </Flex>
    );
  }

  if (category === "video") {
    return (
      <video
        src={blobUrl}
        controls
        style={{ ...style, backgroundColor: "black" }}
        preload="metadata"
      />
    );
  }

  if (category === "audio") {
    return (
      <Flex direction="column" align="center" justify="center" gap="3" style={{ ...style, padding: 32 }}>
        <Text size="3" style={{ color: "white", textAlign: "center" }}>{alt}</Text>
        <audio src={blobUrl} controls style={{ width: "min(480px, 100%)" }} />
      </Flex>
    );
  }

  if (isMain) {
    return (
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        src={blobUrl}
        alt={alt}
        style={style}
      />
    );
  }

  return <img src={blobUrl} alt={alt} style={style} />;
}

export default function ImageGalleryOverlay({
  files,
  initialIndex = 0,
  onClose,
}: ImageGalleryOverlayProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);

  // Setup keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent keyboard events from affecting underlying form
      e.preventDefault();
      e.stopPropagation();

      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft")
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
      if (e.key === "ArrowRight")
        setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [files.length, onClose]);

  // Handle mounting for portal
  useEffect(() => {
    setMounted(true);
    // Prevent body scroll when overlay is open
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  if (!files?.length || !mounted) return null;

  const currentFile = files[currentIndex];

  // Navigation handlers with strict event isolation
  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : files.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < files.length - 1 ? prev + 1 : 0));
  };

  const handleThumbnailClick = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(index);
  };

  // Prevent overlay clicks from reaching the form
  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Dedicated close handler for close button to prevent event conflicts
  const handleCloseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Prevent inner clicks from bubbling and closing the overlay
  const handleInnerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const overlayContent = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999, // Ensure gallery is above everything, including side drawers
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px",
        pointerEvents: "auto", // Ensure overlay captures all pointer events
      }}
      onClick={handleOverlayClick}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <Flex justify="end" style={{ width: "100%", marginBottom: "8px" }}>
        <IconButton
          variant="ghost"
          size="3"
          onClick={handleCloseClick}
          aria-label="Close gallery"
          style={{
            color: "rgba(255, 255, 255, 0.8)",
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            cursor: "pointer",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "rgba(255, 255, 255, 1)";
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
            e.currentTarget.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "rgba(255, 255, 255, 0.8)";
            e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <X size={24} />
        </IconButton>
      </Flex>

      {/* Main content */}
      <Flex
        align="center"
        justify="center"
        style={{
          width: "100%",
          position: "relative",
        }}
        onClick={handleInnerClick}
      >
        {/* Navigation */}
        {files.length > 1 && (
          <>
            <IconButton
              variant="solid"
              size="3"
              onClick={handlePrev}
              aria-label="Previous image"
              style={{
                position: "absolute",
                left: "16px",
                zIndex: 10,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                borderRadius: "9999px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <ChevronLeft size={28} />
            </IconButton>
            <IconButton
              variant="solid"
              size="3"
              onClick={handleNext}
              aria-label="Next image"
              style={{
                position: "absolute",
                right: "16px",
                zIndex: 10,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                color: "white",
                borderRadius: "9999px",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
                e.currentTarget.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.5)";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <ChevronRight size={28} />
            </IconButton>
          </>
        )}

        {/* Image */}
        <Box
          style={{
            width: "100%",
            maxWidth: "1280px",
            height: "70vh", // Constrain height to 70% of viewport
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: "80px",
            paddingRight: "80px",
          }}
          onClick={handleInnerClick}
        >
          <SecureImage
            key={currentIndex}
            file={currentFile}
            alt={currentFile.original_file_name}
            isMain
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              borderRadius: "var(--radius-2)",
            }}
          />
        </Box>
      </Flex>

      {/* Metadata */}
      <Box
        style={{
          width: "100%",
          textAlign: "center",
          paddingTop: "12px",
          paddingBottom: "12px",
          maxWidth: "768px",
          margin: "0 auto",
        }}
        onClick={handleInnerClick}
      >
        <Text
          size="3"
          weight="medium"
          style={{
            color: "rgba(255, 255, 255, 0.9)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {currentFile.original_file_name}
        </Text>
        <Flex
          justify="center"
          align="center"
          gap="4"
          style={{ marginTop: "8px" }}
        >
          <Text
            size="1"
            style={{ color: "rgba(255, 255, 255, 0.6)", marginRight: "10px" }}
          >
            {currentIndex + 1} / {files.length}
          </Text>
          {currentFile.file_size && (
            <Text
              size="1"
              style={{ color: "rgba(255, 255, 255, 0.6)", marginRight: "10px" }}
            >
              {(currentFile.file_size / 1024 / 1024).toFixed(2)} MB
            </Text>
          )}
          {currentFile.file_type && (
            <Text
              size="1"
              style={{ color: "rgba(255, 255, 255, 0.6)", marginRight: "10px" }}
            >
              {currentFile.file_type.split("/")[1].toUpperCase()}
            </Text>
          )}
          {currentFile.created_at && (
            <Text size="1" style={{ color: "rgba(255, 255, 255, 0.6)" }}>
              Uploaded: {new Date(currentFile.created_at).toLocaleDateString()}
            </Text>
          )}
        </Flex>
      </Box>

      {/* Thumbnails */}
      {files.length > 1 && (
        <Box
          style={{ width: "100%", marginTop: "8px" }}
          onClick={handleInnerClick}
        >
          <Box
            style={{
              width: "100%",
              margin: "0 auto",
              maxWidth: "768px",
              overflowX: "auto",
              overflowY: "hidden",
              paddingTop: "8px",
              paddingBottom: "8px",
            }}
            onClick={handleInnerClick}
          >
            <Flex
              gap="3"
              style={{
                paddingLeft: "8px",
                paddingRight: "8px",
                minWidth: "min-content",
                justifyContent: "center",
              }}
            >
              {files.map((file, index) => (
                <IconButton
                  key={`thumb-${file.id}`}
                  variant="ghost"
                  onClick={(e) => handleThumbnailClick(e, index)}
                  aria-label={`View ${file.original_file_name}`}
                  aria-pressed={index === currentIndex}
                  style={{
                    padding: 0,
                    width: "64px",
                    height: "64px",
                    borderRadius: "var(--radius-2)",
                    overflow: "hidden",
                    transition: "all 0.2s",
                    flexShrink: 0,
                    border:
                      index === currentIndex
                        ? "2px solid var(--blue-9)"
                        : "2px solid transparent",
                    opacity: index === currentIndex ? 1 : 0.6,
                    boxShadow:
                      index === currentIndex
                        ? "0 4px 12px rgba(59, 130, 246, 0.2)"
                        : "none",
                  }}
                >
                  <SecureImage
                    file={file}
                    alt={file.original_file_name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </IconButton>
              ))}
            </Flex>
          </Box>
        </Box>
      )}

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );

  // Use portal to render at document root for true full-page overlay
  return createPortal(overlayContent, document.body);
}
